import { PageHeader, FilterBar, LoadingState, DataTable } from '../components/AdminPageUI';
import React, { useState, useEffect } from 'react';
import { Typography, Button, Container, Grid, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow, Paper, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Chip } from '@mui/material';
import { AttachMoney as MoneyIcon } from '@mui/icons-material';
import api from '../utils/Api';
import { toast } from 'react-toastify';

export default function TransportFeesPage() {
  const [loading, setLoading] = useState(true);
  const [fees, setFees] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState('');
  
  // Payment Dialog State
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [selectedFeeRecord, setSelectedFeeRecord] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      let schoolId = '';
      if (userStr) {
        const user = JSON.parse(userStr);
        schoolId = user.school?._id || user.school || '';
      }

      // Pre-fill aggressive check
      const cwRes = await api.get('/term/current-week', { params: { school: schoolId } }).catch(() => null);
      let prefilledTermId = '';
      if (cwRes?.data?.success) {
        prefilledTermId = cwRes.data.data.termId;
      }

      const termRes = await api.get('/term/all', { params: { school: schoolId } });
      const fetchedTerms = termRes.data.allTerms || [];
      setTerms(fetchedTerms);
      
      if (prefilledTermId) {
        setSelectedTermId(prefilledTermId);
      } else if (fetchedTerms.length > 0) {
        setSelectedTermId(fetchedTerms[0]._id);
      }
    } catch (err) {
      toast.error('Failed to load terms');
    } finally {
      setLoading(false);
    }
  };

  const fetchFees = async () => {
    if (!selectedTermId) return;
    try {
      setLoading(true);
      const res = await api.get('/transport/fees', { params: { termId: selectedTermId } });
      setFees(res.data.fees || []);
    } catch (err) {
      toast.error('Failed to fetch fees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedTermId) {
      fetchFees();
    }
  }, [selectedTermId]);

  const handleOpenPayment = (feeRecord) => {
    setSelectedFeeRecord(feeRecord);
    setPaymentAmount(feeRecord.balance.toString());
    setPaymentMethod('cash');
    setReceiptNumber('');
    setOpenPaymentModal(true);
  };

  const handleProcessPayment = async () => {
    if (!paymentAmount || isNaN(paymentAmount) || Number(paymentAmount) <= 0) {
      toast.warning('Please enter a valid payment amount');
      return;
    }

    if (Number(paymentAmount) > selectedFeeRecord.balance) {
      toast.warning('Payment amount cannot exceed the outstanding balance');
      return;
    }

    try {
      setIsSubmitting(true);
      await api.post('/transport/fees/pay', {
        feeId: selectedFeeRecord._id,
        amount: Number(paymentAmount),
        method: paymentMethod,
        receiptNumber
      });
      
      toast.success('Payment recorded successfully!');
      setOpenPaymentModal(false);
      fetchFees();
    } catch (err) {
      toast.error('Failed to record payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title={<>Transport Fees</>} description={<>Review transport charges, balances, and payments.</>}></PageHeader>
      <FilterBar>
<TextField
            select
            size="small"
            fullWidth
            label="Filter by Term"
            value={selectedTermId}
            onChange={(e) => setSelectedTermId(e.target.value)}
          >
            {terms.map(t => (
              <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
            ))}
          </TextField>
      </FilterBar>

      <Card elevation={2}>
        <CardContent>
          {loading ? (
            <LoadingState />
          ) : (
            <DataTable component={Paper} variant="outlined">
              <Table>
                <TableHead sx={{ bgcolor: 'background.default' }}>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell align="right">Total Fee (GHS)</TableCell>
                    <TableCell align="right">Amount Paid (GHS)</TableCell>
                    <TableCell align="right">Balance (GHS)</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No transport fee records found for this term.</Typography>
                        <Typography variant="caption" color="text.secondary">Fee records are automatically generated when a student is enrolled in a route with a default fee.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    fees.map(fee => (
                      <TableRow key={fee._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{fee.student?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{fee.student?.admissionNumber}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fee.totalAmount.toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>{fee.amountPaid.toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ color: fee.balance > 0 ? 'error.main' : 'text.primary', fontWeight: 'bold' }}>{fee.balance.toFixed(2)}</TableCell>
                        <TableCell align="center">
                          {fee.balance === 0 ? (
                            <Chip label="Fully Paid" color="success" size="small" />
                          ) : fee.amountPaid > 0 ? (
                            <Chip label="Partial" color="warning" size="small" />
                          ) : (
                            <Chip label="Unpaid" color="error" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button 
                            variant="outlined" 
                            size="small" 
                            disabled={fee.balance === 0}
                            onClick={() => handleOpenPayment(fee)}
                          >
                            Pay
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </DataTable>
          )}
        </CardContent>
      </Card>

      {/* Payment Processing Modal */}
      <Dialog open={openPaymentModal} onClose={() => setOpenPaymentModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon color="primary" /> Record Transport Payment
        </DialogTitle>
        <DialogContent dividers>
          {selectedFeeRecord && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}  >
                <Paper sx={{ p: 2, bgcolor: 'primary.50', mb: 2 }}>
                  <Typography variant="subtitle2" color="primary">Student: {selectedFeeRecord.student?.name}</Typography>
                  <Typography variant="h6" fontWeight="bold">Outstanding Balance: GHS {selectedFeeRecord.balance.toFixed(2)}</Typography>
                </Paper>
              </Grid>
              
              <Grid size={{ xs: 12 }}  >
                <TextField
                  fullWidth
                  label="Payment Amount"
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">GHS</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}   >
                <TextField
                  select
                  fullWidth
                  label="Payment Method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="momo">Mobile Money</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}   >
                <TextField
                  fullWidth
                  label="Receipt Number (Optional)"
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenPaymentModal(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleProcessPayment}
            disabled={isSubmitting || !paymentAmount}
          >
            {isSubmitting ? 'Processing...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
